import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';
import ConfirmDialog from '../../components/UI/ConfirmDialog';
import { 
  getApplicationById, 
  getApplicationParameters, 
  deleteApplicationParameter 
} from '../../services/applications/applicationsService';
import { ApplicationDto, ParameterDto } from '../../services/applications/types';

const ParametersList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const [application, setApplication] = useState<ApplicationDto | null>(null);
  const [parameters, setParameters] = useState<ParameterDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [parameterToDelete, setParameterToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, auth.user]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const fetchData = async (applicationId: string) => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to view parameters.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch both application details and parameters in parallel
      const [appData, parametersData] = await Promise.all([
        getApplicationById(auth.user || null, applicationId),
        getApplicationParameters(auth.user || null, applicationId)
      ]);
      
      setApplication(appData);
      setParameters(parametersData);
    } catch (err) {
      setError('Failed to fetch parameters. Please try again.');
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteClick = (parameterId: string) => {
    setParameterToDelete(parameterId);
    setDeleteConfirmOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!auth.isAuthenticated || !id || !parameterToDelete) return;
    
    setIsDeleting(true);
    
    try {
      await deleteApplicationParameter(auth.user || null, id, parameterToDelete);
      // Refetch parameters to update the list
      const updatedParameters = await getApplicationParameters(auth.user || null, id);
      setParameters(updatedParameters);
    } catch (err) {
      setError('Failed to delete parameter. Please try again.');
      console.error('Error deleting parameter:', err);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setParameterToDelete(null);
    }
  };
  
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setParameterToDelete(null);
  };
  
  // Function to mask sensitive data like passwords and tokens
  const maskSensitiveContent = (type: string | undefined, content: string | undefined) => {
    if (!content) return 'N/A';
    
    const sensitiveTypes = [
      'TELEGRAM_API_TOKEN', 
      'APPLICATION_KEY', 
      'MICROSOFT_APP_PASSWORD',
      'OMNI_CHAT_API_SECRET'
    ];
    
    if (type && sensitiveTypes.includes(type)) {
      return '••••••••••••••••';
    }
    
    // For non-sensitive types, still limit the display length
    return content.length > 50 ? `${content.substring(0, 50)}...` : content;
  };
  
  const handleBackToApplication = () => {
    navigate(`/applications/${id}`);
  };

  return (
    <div>
      <PageHeader
        title={`Parameters - ${application?.name || 'Application'}`}
        breadcrumbs={[
          { label: "Applications", to: "/applications" },
          { label: application?.name || 'Application', to: `/applications/${id}` },
          { label: "Parameters" }
        ]}
        actions={
          <div className="flex space-x-3">
            <Link to={`/applications/${id}/parameters/create`}>
              <Button variant="primary">
                <i className="fas fa-plus mr-2"></i>
                Add Parameter
              </Button>
            </Link>
            <Button
              variant="secondary"
              onClick={handleBackToApplication}
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Application
            </Button>
          </div>
        }
      />
      
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
          {error}
          <Button 
            variant="ghost" 
            size="sm" 
            className="ml-2"
            onClick={() => id && fetchData(id)}
          >
            Retry
          </Button>
        </div>
      )}
      
      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : parameters.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              No parameters found for this application.
            </p>
            <Link to={`/applications/${id}/parameters/create`}>
              <Button variant="primary">
                Add First Parameter
              </Button>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Content
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {parameters.map((parameter) => (
                  <tr key={parameter.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {parameter.type || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {maskSensitiveContent(parameter.type, parameter.content)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {parameter.created_at ? (
                        new Date(parameter.created_at).toLocaleDateString()
                      ) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {parameter.updated_at ? (
                        new Date(parameter.updated_at).toLocaleDateString()
                      ) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <Link to={`/applications/${id}/parameters/${parameter.id}/edit`}>
                          <Button variant="ghost" size="xs">
                            <i className="fas fa-edit"></i>
                          </Button>
                        </Link>
                        <Button 
                          variant="ghost" 
                          size="xs"
                          onClick={() => parameter.id && handleDeleteClick(parameter.id)}
                        >
                          <i className="fas fa-trash text-red-500"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title="Delete Parameter"
        message="Are you sure you want to delete this parameter? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ParametersList;